import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { cn } from "@/lib/helpers/cn";

import { LockIcon } from "@/component-library/Icons/components/LockIcon";
import { createClassNameString } from "@/component-library/utils";
import { TEENS_AND_KIDS_DIAGNOSTIC_INTERVIEW_90791_ALLOTTED_TIME } from "@/components/Provider/ScheduleAvailability/constants";
import Button from "@/components/UI/Button";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import { AssessmentEnum, CATCH_ALL_INSURANCE_OPTIONS } from "@/core/constants";
import { getPatientProfile } from "@/redux/actions/auth";
import { convertToKebabCase, getAutomationDataAttr } from "@/utils/automationUtils";
import { toTitleCase } from "@/utils/filters";
import { getMemberAssessmentType } from "@/utils/getMemberAssessmentType";
import images from "@/utils/images";

import "../styles.scss";

const NextStepWidget = (props) => {
  const { profile } = props;
  const [steps, setSteps] = useState([]);

  let assessmentNoun = "assessment";
  let timeNeeded = "30 minutes";

  let assessment_type = _.get(profile, "assessment_config.default_assessment");

  if (
    assessment_type === "A5" ||
    assessment_type === AssessmentEnum.A5_ASSESSMENT_TYPE ||
    assessment_type === AssessmentEnum.A5_V2_ASSESSMENT_TYPE
  ) {
    assessmentNoun = "screening";
    timeNeeded = "5 minutes";
  }

  let assessmentNounTitle = toTitleCase(assessmentNoun);

  const thereIsAssignmentTask = (assessment_type, status) => {
    const tasks = profile?.tasks;

    if (tasks && tasks.length > 0) {
      const task = tasks.find((task) => task.assessment_type === assessment_type);

      if (task) {
        return task.status === status;
      }
    }

    return false;
  };

  const buildNextStepsConfig = (profile) => {
    const showChildCare = profile.interested_in_guardianship;
    const carrier = _.get(profile, "primary_insurance.carrier");
    const memberId = _.get(profile, "primary_insurance.member_id");
    const hasPrimaryInsurance = !!memberId || CATCH_ALL_INSURANCE_OPTIONS.includes(carrier);

    const assessmentTitle = showChildCare
      ? "Your Fast Track to Better Mental Health"
      : "It’s Time to Complete Your " + assessmentNounTitle;

    const assessmentContent = showChildCare ? (
      <>
        If you'd also like to receive care from your own dedicated team, click the button below to
        take the next step.
      </>
    ) : (
      <>
        The <b>aptihealth {assessmentNoun}</b> is an important first step on your journey to better
        health! It’s important to complete the comprehensive {assessmentNoun} prior to your first
        visit. Completing the aptihealth {assessmentNoun} will help us get to know you better. Once
        we understand your needs, we’ll connect you with a care team that’s dedicated to your
        success.
      </>
    );

    const assessmentSubContent = showChildCare ? (
      <></>
    ) : (
      <>
        To take the next step on your journey, select ‘Start {assessmentNounTitle}’ below! The{" "}
        {assessmentNoun} will take approximately {timeNeeded}. You can pause and resume at any time.
      </>
    );

    return [
      {
        button: {
          action: () => {
            props.history.push("/app/teens-and-kids/program-details");
          },
          icon: "./icons/user-icon.svg",
          text: "Add Child",
        },
        className: "col-lg-6 col-sm-12 col-md-6",
        description: [
          {
            content: (
              <>
                To get your child care with aptihealth, click the Add Child button to complete their
                registration.
              </>
            ),
          },
        ],
        shouldShow: !profile.assessment_status && showChildCare,
        subClassName: "dashboard-widget-card-pri",
        title: "aptihealth's Teens + Kids Care Program",
      },
      {
        button: hasPrimaryInsurance
          ? {
              // TODO update button path based on child care condition
              action: () => {
                props.history.push(
                  `/app/assessment?assessmentType=${getMemberAssessmentType(profile)}`,
                );
              },
              icon: "./icons/common/task/task-white-none.svg",
              text:
                (profile.assessment_started_date ? "Continue " : "Start ") + assessmentNounTitle,
            }
          : {
              action: () => {
                props.history.push("/app/patient-insurance");
              },
              text: "Complete Insurance",
            },
        className: showChildCare ? "col-lg-6 col-sm-12 col-md-6" : "col-12",
        description: [
          {
            content: assessmentContent,
          },
          {
            className: "next-step-sub-desc",
            content: assessmentSubContent,
          },
        ],
        icon: !showChildCare && "./icons/common/task/task-white-sec.svg",
        shouldShow: !profile.assessment_status && profile.status !== "INACTIVE",
        subClassName: "dashboard-widget-card-sec",
        testId: "member-home-next-step-assessment",
        title: assessmentTitle,
      },
      {
        button: {
          action: () => {
            props.history.push("/app/schedule/assign?mode=new");
          },
          icon: "./icons/common/schedule/schedule-white-none.svg",
          text: "Schedule",
        },
        className: "col-12",
        description: [
          {
            content: (
              <>
                It’s time to schedule your first session, your <b>therapist consultation</b> with an
                aptihealth intake coordinator. During your consultation, you’ll meet with your
                intake coordinator to review your goals for your first 90 days of therapy. They’ll
                match you with a licensed therapist and a licensed prescriber, if needed.
              </>
            ),
          },
          {
            className: "next-step-sub-desc",
            content: (
              <>
                To take the next step on your journey, select ‘Schedule’ below! The consultation
                will take approximately{" "}
                {profile.group_type === "teens_kids_program"
                  ? TEENS_AND_KIDS_DIAGNOSTIC_INTERVIEW_90791_ALLOTTED_TIME
                  : 45}{" "}
                minutes.
              </>
            ),
          },
        ],
        icon: "./icons/common/task/task-white-pri.svg",
        shouldShow:
          profile.assessment_status &&
          !profile.schedule_video_call &&
          !profile.initial_video_consult &&
          !profile.is_wait_listed &&
          !profile.is_scheduling_restricted,
        subClassName: "dashboard-widget-card-pri",
        title: "It’s Time to Schedule Your Therapist Consultation",
      },
      {
        button: {
          action: () => {
            props.history.push("/app/assessment?assessmentType=A5-R");
          },
          text: "Start Questions",
          type: "primary",
        },
        className: "col-12",
        description: [
          {
            content: (
              <>
                We’ll ask you to do this every 90 days while in care. Your care team learns what’s
                changed to celebrate your progress or adjust your care.
              </>
            ),
          },
        ],
        footer: {
          content: (
            <>
              <div className="widget-footer-text">
                Only your care team can see your answers. You can pause and resume at any time.
              </div>
            </>
          ),
          footerClassName: "NextStepWidget-a5r_footer",
          iconComponent: (
            <LockIcon
              className="NextStepWidget-a5r_lock_icon"
              iconSize={ICON_SIZES.SMALL}
              iconType={ICON_TYPES.OUTLINE}
            />
          ),
        },
        shouldShow: thereIsAssignmentTask("A5-R", "ASSIGNED"),
        subClassName: "dashboard-widget-card-sec",
        title: "It's Time to Answer a Few Questions",
      },
      {
        button: {
          action: () => {
            props.history.push("/app/assessment?assessmentType=A5-R");
          },
          text: "Finish Questions",
          type: "primary",
        },
        className: "col-12",
        description: [
          {
            content: (
              <>
                Your follow-up questionnaire is still in progress. Finish it so your care team can
                learn what’s changed to celebrate your progress or adjust your care.
              </>
            ),
          },
        ],
        footer: {
          content: (
            <>
              <div className="widget-footer-text">
                Only your care team can see your answers. You can pause and resume at any time.
              </div>
            </>
          ),
          footerClassName: "NextStepWidget-a5r_footer",
          iconComponent: (
            <LockIcon
              className="NextStepWidget-a5r_lock_icon"
              iconSize={ICON_SIZES.SMALL}
              iconType={ICON_TYPES.OUTLINE}
            />
          ),
        },
        shouldShow: thereIsAssignmentTask("A5-R", "IN_PROGRESS"),
        subClassName: "dashboard-widget-card-sec",
        testId: "member-home-next-step-a5r",
        title: "Finish your Questionnaire",
      },
    ];
  };

  useEffect(() => {
    if (profile) {
      const stepsToSet = buildNextStepsConfig(profile).filter((step) => step.shouldShow);

      setSteps(stepsToSet);
    }
  }, [profile]);

  const renderNextSteps = (steps) => {
    function getActionButton(step) {
      const buttonType = step.button.type || "transparent";

      switch (buttonType) {
        case "primary":
          return (
            <Button
              className="Btn Btn-xs Btn-xs-2 Btn-otl-white Btn-primary tw:font-bold"
              onClick={step.button.action}
              style={{
                color: "#4545BA",
                padding: "9px 16px",
                textDecoration: "none",
              }}
            >
              <div className="txt-white d-flex">
                {step.button.icon && <img alt="" src={images(step.button.icon)} />}

                <div className={step.button.icon ? "ml-2 mt-1 mb-1" : "mt-1 mb-1"}>
                  {step.button.text}
                </div>
              </div>
            </Button>
          );

        case "transparent":

        default:
          return (
            <Button
              className="Btn Btn-xs Btn-xs-2 Btn-rounded Btn-otl-white Btn-transparent tw:font-bold"
              data-e2e={`NextStepActionButton_${convertToKebabCase(step.button.text)}`}
              onClick={step.button.action}
              style={{ padding: "1px 10px", textDecoration: "none" }}
            >
              <div className="txt-white d-flex">
                {step.button.icon && <img alt="" src={images(step.button.icon)} />}

                <div className={step.button.icon ? "ml-2 mt-1 mb-1" : "mt-1 mb-1"}>
                  {step.button.text}
                </div>
              </div>
            </Button>
          );
      }
    }

    return (
      <>
        {steps.map((step, stepIndex) => {
          return (
            <div
              className={step.className ? step.className : ""}
              key={`step-${stepIndex}`}
              {...getAutomationDataAttr(step.testId)}
            >
              <div
                className={cn(
                  "dashboard-widget-card px-lg-4 px-3 py-3 mb-3 text-white",
                  step.subClassName,
                )}
              >
                <div className="fs-lg-22 fs-18 mb-1 d-inline-flex tw:font-bold">{step.title}</div>

                <div className="py-2 d-flex" key={`next-step-widget-${step.title}`}>
                  {step.icon && (
                    <div className="pr-lg-3 pr-2">
                      <img alt={stepIndex} src={images(step.icon)} />
                    </div>
                  )}

                  <div>
                    {step.description &&
                      step.description.map((paragraph, index) => {
                        return (
                          <div
                            className={cn("mb-3", paragraph.className)}
                            key={`paragraph-${index}`}
                          >
                            {paragraph.content}
                          </div>
                        );
                      })}

                    {step.button && (
                      <div className="d-lg-block d-none">{getActionButton(step)}</div>
                    )}
                  </div>
                </div>

                {step.button && (
                  <div className="d-lg-none d-flex tw:justify-center">{getActionButton(step)}</div>
                )}

                {step.footer && (
                  <div className="mt-3">
                    <div
                      className={createClassNameString(["d-flex", step.footer?.footerClassName])}
                    >
                      {step.footer?.iconComponent}

                      <div className="ml-2">{step.footer.content}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return <>{renderNextSteps(steps)}</>;
};

export default connect(null, { getPatientProfile })(withRouter(NextStepWidget));
